import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Header from "../../components/header";
import Footer from "../../components/footer";
import { montessoriApps } from "../../data/montessori";
import { ROUTE } from "../../utils/constants";
import { useNavigation } from "../../utils/functions";

import "./styles.css";

const RAINBOW_COLORS = ["#c8714b", "#e7a23c", "#d9b98a", "#8fb08a", "#7fa6b5"];
const PRINCIPLES = ["autonomy", "concrete", "privacy"];

function MontessoriRainbow() {
  return (
    <svg className="mh-rainbow" viewBox="0 0 220 120" aria-hidden="true" focusable="false">
      {RAINBOW_COLORS.map((color, index) => {
        const radius = 100 - index * 17;
        return (
          <path
            key={color}
            d={`M ${110 - radius} 113 A ${radius} ${radius} 0 0 1 ${110 + radius} 113`}
            fill="none"
            stroke={color}
            strokeWidth="14"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

function MontessoriAppCard({ app }) {
  const { t } = useTranslation();
  const title = t(app.titleKey);

  return (
    <article className="mh-card" style={{ "--mh-accent": app.accent, "--mh-soft": app.soft }}>
      <a className="mh-card-preview" href={app.url} tabIndex={-1} aria-hidden="true">
        <span className="mh-card-dot mh-card-dot-a" />
        <span className="mh-card-dot mh-card-dot-b" />
        <span className="mh-card-emoji">{app.emoji}</span>
      </a>

      <div className="mh-card-body">
        <div className="mh-card-heading">
          <h3>{title}</h3>
          <span className="mh-card-tag">{t(app.tagKey)}</span>
        </div>

        <p className="mh-card-description">{t(app.descriptionKey)}</p>

        <ul className="mh-card-features">
          {app.featureKeys.map((featureKey) => (
            <li key={featureKey}>{t(featureKey)}</li>
          ))}
        </ul>

        <a className="mh-card-button" href={app.url}>
          {t("montessori.open")}
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </article>
  );
}

function MontessoriHubPage() {
  const { t } = useTranslation();
  const goTo = useNavigation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = "Basilisco Montessori";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="App mh-page">
      <Header />

      <main className="mh-content">
        <section className="mh-hero">
          <div className="mh-hero-text">
            <button type="button" className="mh-back" onClick={() => goTo(ROUTE.project)}>
              ← {t("montessori.back")}
            </button>
            <span className="mh-eyebrow">{t("montessori.eyebrow")}</span>
            <h1>
              Basilisco <span>Montessori</span>
            </h1>
            <p>{t("montessori.subtitle")}</p>
            <ul className="mh-tags">
              <li>{t("montessori.tags.free")}</li>
              <li>{t("montessori.tags.noAccount")}</li>
              <li>{t("montessori.tags.devices")}</li>
            </ul>
          </div>
          <div className="mh-hero-art">
            <MontessoriRainbow />
          </div>
        </section>

        <section className="mh-apps" aria-labelledby="mh-apps-title">
          <div className="mh-section-heading">
            <h2 id="mh-apps-title">{t("montessori.appsTitle")}</h2>
            <span>{t("montessori.appsCount", { count: montessoriApps.length })}</span>
          </div>
          <div className="mh-grid">
            {montessoriApps.map((app) => (
              <MontessoriAppCard app={app} key={app.slug} />
            ))}
          </div>
          <p className="mh-language-note">{t("montessori.languageNote")}</p>
        </section>

        <section className="mh-principles">
          {PRINCIPLES.map((key) => (
            <div className="mh-principle" key={key}>
              <span className="mh-principle-icon" aria-hidden="true">
                {t(`montessori.principles.${key}.icon`)}
              </span>
              <div>
                <h3>{t(`montessori.principles.${key}.title`)}</h3>
                <p>{t(`montessori.principles.${key}.text`)}</p>
              </div>
            </div>
          ))}
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default MontessoriHubPage;
